import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Col,
  Collapse,
  DatePicker,
  Input,
  InputNumber,
  Row,
  Select,
} from "antd";
import { FilterOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import moment from "moment";
import { orderBy } from "lodash";
import { useSelector } from "react-redux";
import Localize, { l } from "../../libs/localization";
import Form from "../Form/Form";
import Tree from "../Tree/Tree";
import Label from "../Label/Label";
import CompanyAutocomplete from "../CompanyAutocomplete/CompanyAutocomplete";
import BrandAutocomplete from "../BrandAutocomplete/BrandAutocomplete";
import PlaceAutocomplete from "../PlaceAutocomplete/PlaceAutocomplete";
import FinancialForm from "../../pages/form/FinancialForm";
import InfoForm from "../../pages/form/InfoForm";
import InvestmentForm from "../../pages/form/InvestmentForm";
import InvestmentRoundsForm from "../../pages/form/InvestmentRoundsForm";
import { translateRegistryOnView } from "../../libs/utils";
import useRegistryData from "../../hooks/useRegistryData";
import usePlacesData from "../../hooks/usePlacesData";
import useFlatEntityData from "../../hooks/useFlatEntityData";

const { Panel } = Collapse;
const { RangePicker } = DatePicker;

const CreaFilters = ({
  dataForm,
  setDataForm,
  loadingSearch,
  renderExtraActions,
  features = {},
  permissions = {},
  tenant,
  brandAdvertiserFilters = [],
  setBrandAdvertiserFilters,
  isHR,
}) => {
  const [showOther, setShowOther] = useState(false);
  const [open, setOpen] = useState([1]);
  const [durationType, setDurationType] = useState("all");
  const [currentBrandAdvertiserFilter, setCurrentBrandAdvertiserFilter] =
    useState({
      platform: "",
      year: new Date().getFullYear(),
      property: "reach",
      surfaces: [],
      min: undefined,
      max: undefined,
    });

  const { data: categoriesData } = useFlatEntityData("crea_category");
  const { mainCategory, sector, otherCategories } = useRegistryData();
  const { citiesList, regionsList, provincesList, loadPlaces } = usePlacesData();
  const token = useSelector((state) => state.auth.token);
  const language = useSelector((state) => state.app.language);

  useEffect(() => {
    if (showOther) {
      loadPlaces();
    }
  }, [showOther, loadPlaces]);

  const callback = (key) => {
    setOpen(key);
  };

  const sortedSectors = useMemo(() => {
    const sortTree = (tree) => {
      if (!tree || !Array.isArray(tree)) return [];
      const sorted = orderBy(
        tree,
        (item) => {
          const translated =
            translateRegistryOnView(item._id, "Sector", false, language) ||
            item.key;
          return translated
            ? translated
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .toLowerCase()
            : "";
        },
        ["asc"],
      );
      return sorted.map((item) => ({
        ...item,
        children: sortTree(item.children),
      }));
    };
    return sortTree(sector);
  }, [sector, language]);

  return (
    <div className="crea-filters">
      <Form
        name="crea-filter-form"
        initialValues={dataForm}
        onChange={({ values, errors }) => {
          setDataForm({
            ...dataForm,
            ...values,
            ...errors,
          });
        }}
      >
        {({ updateValue }) => (
          <>
            <Row gutter={16}>
              <Col span={8}>
                <Label
                  className="filter-color"
                  value={l("PAGES.CREA.SEARCH.TITLE_FILTER")}
                />
                <Input
                  placeholder={l("PAGES.CREA.SEARCH.TITLE_FILTER")}
                  value={dataForm.title}
                  onChange={(e) => updateValue({ key: "title", value: e.target.value })}
                />
              </Col>
              <Col span={8}>
                <Label
                  className="filter-color"
                  value={l("PAGES.SEARCH.ORDER.CATEGORY")}
                />
                <Select
                  mode="multiple"
                  allowClear={true}
                  style={{ width: "100%" }}
                  value={dataForm.crea_category}
                  placeholder={l("PAGES.SEARCH.ORDER.CATEGORY")}
                  onChange={(value) => updateValue({ key: "crea_category", value })}
                  showSearch
                  options={
                    categoriesData &&
                    orderBy(
                      categoriesData.set || categoriesData.enum || [],
                      ["id"],
                      ["asc"]
                    )
                      .filter((o) => o.active)
                      .map((e) => ({
                        value: e.id,
                        label:
                          translateRegistryOnView(
                            e.id,
                            "crea_category",
                            true,
                            language
                          ) || e.id,
                        key: e.id,
                      }))
                  }
                />
              </Col>
              <Col span={8}>
                <Label
                  className="filter-color"
                  value={l("PAGES.CREA.SEARCH.DATE_RANGE")}
                />
                <RangePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  allowEmpty={[true, true]}
                  placeholder={[l("DATE.START_DATE"), l("DATE.END_DATE")]}
                  value={[
                    dataForm.date_start ? moment(dataForm.date_start) : null,
                    dataForm.date_end ? moment(dataForm.date_end) : null,
                  ]}
                  onChange={(dates) => {
                    const [start, end] = dates || [];
                    updateValue({
                      key: "date_start",
                      value: start ? start.format("YYYY-MM-DD") : undefined,
                    });
                    updateValue({
                      key: "date_end",
                      value: end ? end.format("YYYY-MM-DD") : undefined,
                    });
                  }}
                />
              </Col>
            </Row>

            <Row justify="space-between" align="bottom">
              <Col>
                <Button
                  type="link"
                  onClick={() => setShowOther(!showOther)}
                  className="secondary mt-3 pl-2"
                >
                  <FilterOutlined className="mr-1" />
                  {l(`COMMON.${!showOther ? "OTHER_FILTERS" : "CLOSE"}`)}
                </Button>
              </Col>
              <Col>{renderExtraActions && renderExtraActions()}</Col>
            </Row>

            {showOther && (
              <div className="animation fadein-right slow" style={{ marginTop: 20 }}>
                <Collapse onChange={callback} ghost={true} activeKey={open}>
                  <Panel
                    header={l("PAGES.CREA.SEARCH.CREATIVITY_FILTERS")}
                    key="1"
                    className="pt-3"
                  >
                    <Row gutter={16}>
                      <Col span={8}>
                        <Label
                          className="filter-color"
                          value={l("COMMON.BRAND")}
                        />
                        <BrandAutocomplete
                          singleValue={true}
                          value={dataForm.brand_id ? [dataForm.brand_id] : []}
                          onChange={(value) => {
                            const selectedId = value && value[0]?.id;
                            updateValue({ key: "brand_id", value: selectedId });
                          }}
                        />
                      </Col>
                      <Col span={8}>
                        <Label
                          className="filter-color"
                          value={l("PAGES.CREA.SEARCH.CREATIVE_AGENCY")}
                        />
                        <CompanyAutocomplete
                          token={token}
                          singleValue={true}
                          value={dataForm.agency_id ? [{ id: dataForm.agency_id }] : []}
                          onChange={(value) => {
                            const selectedId = value && value[0]?.id;
                            updateValue({ key: "agency_id", value: selectedId });
                          }}
                        />
                      </Col>
                      <Col span={8}>
                        <Label
                          className="filter-color"
                          value={l("PAGES.CREA.SEARCH.DURATION")}
                        />
                        <Select
                          style={{ width: "100%" }}
                          value={durationType}
                          onChange={(val) => {
                            setDurationType(val);
                            if (val === "all") {
                              updateValue({ key: "duration_min", value: undefined });
                              updateValue({ key: "duration_max", value: undefined });
                            } else if (val === "short") {
                              updateValue({ key: "duration_min", value: 0 });
                              updateValue({ key: "duration_max", value: 14 });
                            } else if (val === "medium") {
                              updateValue({ key: "duration_min", value: 15 });
                              updateValue({ key: "duration_max", value: 30 });
                            } else if (val === "long") {
                              updateValue({ key: "duration_min", value: 31 });
                              updateValue({ key: "duration_max", value: undefined });
                            }
                          }}
                          options={[
                            { value: "all", label: l("PAGES.CREA.SEARCH.DURATION.ALL") },
                            { value: "short", label: l("PAGES.CREA.SEARCH.DURATION.SHORT") },
                            { value: "medium", label: l("PAGES.CREA.SEARCH.DURATION.MEDIUM") },
                            { value: "long", label: l("PAGES.CREA.SEARCH.DURATION.LONG") },
                            { value: "custom", label: l("PAGES.CREA.SEARCH.DURATION.CUSTOM") },
                          ]}
                        />
                        {durationType === "custom" && (
                          <Row gutter={8} className="mt-2">
                            <Col span={12}>
                              <InputNumber
                                style={{ width: "100%" }}
                                min={0}
                                placeholder={l("PAGES.CREA.SEARCH.MIN_DURATION")}
                                value={dataForm.duration_min}
                                onChange={(value) => updateValue({ key: "duration_min", value })}
                              />
                            </Col>
                            <Col span={12}>
                              <InputNumber
                                style={{ width: "100%" }}
                                min={0}
                                placeholder={l("PAGES.CREA.SEARCH.MAX_DURATION")}
                                value={dataForm.duration_max}
                                onChange={(value) => updateValue({ key: "duration_max", value })}
                              />
                            </Col>
                          </Row>
                        )}
                      </Col>
                    </Row>

                    <Row gutter={16} className="mt-3">
                      <Col span={8}>
                        <Label
                          className="filter-color"
                          value={l("PAGES.CREA.SEARCH.INFOS")}
                        />
                        <Input
                          placeholder={l("PAGES.CREA.SEARCH.INFOS")}
                          value={dataForm.infos}
                          onChange={(e) => updateValue({ key: "infos", value: e.target.value })}
                        />
                      </Col>
                    </Row>
                  </Panel>

                  <Panel
                    header={l("PAGES.CREA.SEARCH.COMPANY_FILTERS")}
                    key="2"
                    className="pt-3"
                  >
                    <Row gutter={16}>
                      <Col span={8}>
                        <Label
                          className="filter-color"
                          value={l("COMMON.COMPANY")}
                        />
                        <CompanyAutocomplete
                          token={token}
                          value={dataForm.company_companyIds || []}
                          onChange={(value) => {
                            updateValue({ key: "company_companyIds", value });
                          }}
                        />
                      </Col>
                      <Col span={8}>
                        <Label
                          className="filter-color"
                          value={l("PAGES.SEARCH.ORDER.CATEGORY")}
                        />
                        <Select
                          allowClear={true}
                          style={{ width: "100%" }}
                          value={dataForm.company_main_category}
                          placeholder={l("PAGES.SEARCH.ORDER.CATEGORY")}
                          onChange={(value) => updateValue({ key: "company_main_category", value })}
                          showSearch
                          options={
                            mainCategory?.enum &&
                            orderBy(mainCategory.enum, ["id"], ["asc"])
                              .filter((o) => o.active)
                              .map((e) => ({
                                value: e.id,
                                label:
                                  translateRegistryOnView(
                                    e.id,
                                    "main_category",
                                    true,
                                    language
                                  ) || e.id,
                                key: e.id,
                              }))
                          }
                        />
                      </Col>
                      <Col span={8}>
                        <Label
                          className="filter-color"
                          value={l("PAGES.SEARCH.ORDER.SECTOR")}
                        />
                        <Tree
                          translateEntity="Sector"
                          valueData={dataForm.company_sector}
                          datatree={sortedSectors}
                          onChange={(value) => updateValue({ key: "company_sector", value })}
                          onSelect={(value) => updateValue({ key: "company_sector", value })}
                          language={language}
                        />
                      </Col>
                    </Row>

                    <Row gutter={16} className="mt-3">
                      <Col span={8}>
                        <Label
                          className="filter-color"
                          value={l("COMMON.CITY")}
                        />
                        <PlaceAutocomplete
                          disableOptions={loadingSearch}
                          data={citiesList?.sort().filter((el) => el !== "")}
                          value={dataForm.company_city}
                          onChange={(value) => updateValue({ key: "company_city", value })}
                        />
                      </Col>
                      <Col span={8}>
                        <Label
                          className="filter-color"
                          value={l("COMMON.PROVINCE")}
                        />
                        <PlaceAutocomplete
                          disableOptions={loadingSearch}
                          data={provincesList?.sort().filter((el) => el !== "")}
                          value={dataForm.company_province}
                          onChange={(value) => updateValue({ key: "company_province", value })}
                        />
                      </Col>
                      <Col span={8}>
                        <Label
                          className="filter-color"
                          value={l("COMMON.REGION")}
                        />
                        <PlaceAutocomplete
                          disableOptions={loadingSearch}
                          data={regionsList?.sort().filter((el) => el !== "")}
                          value={dataForm.company_region}
                          onChange={(value) => updateValue({ key: "company_region", value })}
                        />
                      </Col>
                    </Row>

                    <Row gutter={16} className="mt-3">
                      {tenant !== "spain" && (
                        <Col span={8}>
                          <Label
                            className="filter-color"
                            value={Localize("PAGES.SEARCH.ORDER.OTHER_CAT")}
                          />
                          <Select
                            mode="multiple"
                            allowClear={true}
                            style={{ width: "100%" }}
                            value={dataForm.other_categories}
                            onChange={(value) =>
                              updateValue({ key: "other_categories", value })
                            }
                            showSearch
                            options={
                              otherCategories?.set &&
                              orderBy(otherCategories.set, "id")
                                .filter((e) => e.id.length > 0)
                                .map((e) => ({
                                  value: e.id,
                                  label:
                                    translateRegistryOnView(
                                      e.id,
                                      "other_categories",
                                      true,
                                      language
                                    ) || e.id,
                                  key: e.id,
                                }))
                            }
                          />
                        </Col>
                      )}
                      {features.atecoCode && (
                        <Col span={8}>
                          <Label
                            className="filter-color"
                            value={Localize("PAGES.SEARCH.ORDER.ATECO_CODE")}
                          />
                          <Input
                            value={dataForm.ateco_code}
                            onChange={(e) =>
                              updateValue({
                                key: "ateco_code",
                                value: e.target.value,
                              })
                            }
                          />
                        </Col>
                      )}
                    </Row>

                    {/* Nested Company Sub-filters Collapse */}
                    <Collapse ghost={true} style={{ marginTop: 16 }}>
                      {permissions.companyFinancials && features.companyFinancials && (
                        <Panel
                          header={Localize("PAGES.SEARCH.ORDER.FINANCIAL_DATA")}
                          key="financials"
                          className="pt-3"
                        >
                          <FinancialForm
                            onChange={(key, value) => updateValue({ key, value })}
                            dataForm={dataForm}
                          />
                        </Panel>
                      )}

                      <Panel
                        header={Localize("PAGES.SEARCH.ORDER.ADDITION_INFO")}
                        key="info"
                        className="pt-3"
                      >
                        <InfoForm
                          onChange={(key, value) => updateValue({ key, value })}
                          dataForm={dataForm}
                          isHR={isHR}
                        />
                      </Panel>

                      {features.companyInvestments && permissions.companyInvestments && (
                        <Panel
                          header={Localize("PAGES.SEARCH.ORDER.ADEVERTISING_INVESTMENTS")}
                          key="investments"
                          className="pt-3"
                        >
                          <InvestmentForm
                            onChange={(key, value) => updateValue({ key, value })}
                            dataForm={dataForm}
                          />
                        </Panel>
                      )}

                      <Panel
                        header={Localize("PAGES.SEARCH.SOCIAL_ADS")}
                        key="social_ads"
                        className="pt-3"
                      >
                        {brandAdvertiserFilters.map((filter, index) => (
                          <Row gutter={16} key={index} style={{ marginBottom: 16 }}>
                            <Col span={4}>
                              <Label
                                className="filter-color"
                                value={Localize("BRAND_ADVERTISERS.PLATFORM")}
                              />
                              <div>{filter.platform || Localize("COMMON.ALL")}</div>
                            </Col>
                            <Col span={3}>
                              <Label
                                className="filter-color"
                                value={Localize("COMMON.YEAR")}
                              />
                              <div>{filter.year}</div>
                            </Col>
                            <Col span={3}>
                              <Label
                                className="filter-color"
                                value={Localize("PAGES.SEARCH.PROPERTY")}
                              />
                              <div>
                                {filter.property === "reach"
                                  ? Localize("PAGES.SEARCH.REACH")
                                  : Localize("BRAND_ADVERTISERS.N_ADS")}
                              </div>
                            </Col>
                            <Col span={4}>
                              <Label
                                className="filter-color"
                                value={Localize("COMMON.MIN")}
                              />
                              <div>{filter.min || "-"}</div>
                            </Col>
                            <Col span={4}>
                              <Label
                                className="filter-color"
                                value={Localize("COMMON.MAX")}
                              />
                              <div>{filter.max || "-"}</div>
                            </Col>
                            <Col span={5}>
                              <Button
                                type="default"
                                onClick={() =>
                                  setBrandAdvertiserFilters(
                                    brandAdvertiserFilters.filter(
                                      (_, i) => i !== index
                                    )
                                  )
                                }
                                style={{ marginTop: 24 }}
                              >
                                <MinusOutlined /> {Localize("COMMON.REMOVE")}
                              </Button>
                            </Col>
                          </Row>
                        ))}
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                          <Col span={4}>
                            <Label
                              className="filter-color"
                              value={Localize("BRAND_ADVERTISERS.PLATFORM")}
                            />
                            <Select
                              allowClear
                              style={{ width: "100%" }}
                              value={currentBrandAdvertiserFilter.platform || undefined}
                              placeholder={Localize("COMMON.ALL")}
                              onChange={(value) =>
                                setCurrentBrandAdvertiserFilter({
                                  ...currentBrandAdvertiserFilter,
                                  platform: value || "",
                                })
                              }
                              options={[
                                {
                                  value: "META",
                                  label: Localize("BRAND_ADVERTISERS.PLATFORM_META"),
                                },
                                {
                                  value: "GOOGLE",
                                  label: Localize("BRAND_ADVERTISERS.PLATFORM_GOOGLE"),
                                },
                              ]}
                            />
                          </Col>
                          <Col span={3}>
                            <Label
                              className="filter-color"
                              value={Localize("COMMON.YEAR")}
                            />
                            <InputNumber
                              style={{ width: "100%" }}
                              min={2000}
                              max={new Date().getFullYear()}
                              value={currentBrandAdvertiserFilter.year}
                              onChange={(value) =>
                                setCurrentBrandAdvertiserFilter({
                                  ...currentBrandAdvertiserFilter,
                                  year: value,
                                })
                              }
                            />
                          </Col>
                          <Col span={3}>
                            <Label
                              className="filter-color"
                              value={Localize("PAGES.SEARCH.PROPERTY")}
                            />
                            <Select
                              style={{ width: "100%" }}
                              value={currentBrandAdvertiserFilter.property}
                              onChange={(value) =>
                                setCurrentBrandAdvertiserFilter({
                                  ...currentBrandAdvertiserFilter,
                                  property: value,
                                })
                              }
                              options={[
                                {
                                  value: "reach",
                                  label: Localize("PAGES.SEARCH.REACH"),
                                },
                                {
                                  value: "adsCount",
                                  label: Localize("BRAND_ADVERTISERS.N_ADS"),
                                },
                              ]}
                            />
                          </Col>
                          <Col span={4}>
                            <Label
                              className="filter-color"
                              value={Localize("COMMON.MIN")}
                            />
                            <InputNumber
                              style={{ width: "100%" }}
                              min={0}
                              value={currentBrandAdvertiserFilter.min}
                              onChange={(value) =>
                                setCurrentBrandAdvertiserFilter({
                                  ...currentBrandAdvertiserFilter,
                                  min: value,
                                })
                              }
                            />
                          </Col>
                          <Col span={4}>
                            <Label
                              className="filter-color"
                              value={Localize("COMMON.MAX")}
                            />
                            <InputNumber
                              style={{ width: "100%" }}
                              min={1}
                              value={currentBrandAdvertiserFilter.max}
                              onChange={(value) =>
                                setCurrentBrandAdvertiserFilter({
                                  ...currentBrandAdvertiserFilter,
                                  max: value,
                                })
                              }
                            />
                          </Col>
                          <Col span={5}>
                            <Button
                              type="primary"
                              disabled={
                                !currentBrandAdvertiserFilter.year ||
                                (!currentBrandAdvertiserFilter.min &&
                                  !currentBrandAdvertiserFilter.max)
                              }
                              onClick={() => {
                                setBrandAdvertiserFilters([
                                  ...brandAdvertiserFilters,
                                  { ...currentBrandAdvertiserFilter },
                                ]);
                                setCurrentBrandAdvertiserFilter({
                                  platform: "",
                                  year: new Date().getFullYear(),
                                  property: "reach",
                                  surfaces: [],
                                  min: undefined,
                                  max: undefined,
                                });
                              }}
                              style={{ marginTop: 24 }}
                            >
                              <PlusOutlined /> {Localize("COMMON.ADD")}
                            </Button>
                          </Col>
                        </Row>
                      </Panel>

                      {permissions.investmentRounds && features.investmentRounds && (
                        <Panel
                          header={Localize("PAGES.SEARCH.ORDER.INVESTMENT_ROUNDS")}
                          key="rounds"
                          className="pt-3"
                        >
                          <InvestmentRoundsForm
                            onChange={(key, value) => updateValue({ key, value })}
                            dataForm={dataForm}
                          />
                        </Panel>
                      )}
                    </Collapse>
                  </Panel>
                </Collapse>
              </div>
            )}
          </>
        )}
      </Form>
    </div>
  );
};

export default CreaFilters;
